import React, { useCallback, useEffect, useMemo, useState } from 'react';

import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import RestoreIcon from '@mui/icons-material/Restore';
import {
	Alert,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from '@mui/material';

import { useApiClient, useApiEnvironments, usePersistedSetting, usePersistedSize, useToastService } from '@@common';

import { EnvironmentModel, FlowClient } from '../../../api/api';
import DialogHeaderActions from '../../../components/DialogHeaderActions';
import { useNotifications } from '../../../components/NotificationContext';
import { SchemaValueModel } from '../Models/SchemaValueModel';
import { generateSampleJsonFromSchema, validateRequiredFields } from '../Services/JsonSchemaUtils';
import JsonObjectInput from './Inputs/JsonObjectInput';

interface RunFlowModalProps {
	open: boolean;
	onClose: () => void;
	onSuccess?: (instanceId: string) => void;
	flowId: string;
	definitionId?: string;
	flowName: string;
	mode?: 'run' | 'resume';
	environmentId?: string;
	inputSchemaValue?: SchemaValueModel;
}

const DIALOG_SIZE_KEY = 'runFlowModal.size';
const DIALOG_MAXIMIZED_KEY = 'runFlowModal.maximized';
const selectMenuProps = {
	sx: { zIndex: 1501 },
};
const dialogHeaderIconButtonSx = {
	width: 32,
	height: 32,
	p: 0,
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	'& .MuiSvgIcon-root': {
		display: 'block',
		fontSize: 20,
	},
};

export default function RunFlowModal({
	open,
	onClose,
	onSuccess,
	flowId,
	definitionId,
	flowName,
	mode = 'run',
	environmentId: initialEnvironmentId,
	inputSchemaValue,
}: RunFlowModalProps) {
	const [selectedEnvironmentId, setSelectedEnvironmentId] = useState<string>('');
	const [isRunning, setIsRunning] = useState(false);
	const [runSynchronous, setRunSynchronous] = useState(mode === 'resume');
	const [storeTransitions, setStoreTransitions] = usePersistedSetting('flow-run-modal.storeTransitions', true);
	const [storeVariables, setStoreVariables] = usePersistedSetting('flow-run-modal.storeVariables', false);
	const [isMaximized, setIsMaximized] = usePersistedSetting(DIALOG_MAXIMIZED_KEY, false);
	const { ref: paperRef, size: storedSize } = usePersistedSize(DIALOG_SIZE_KEY, open, { width: '900px', height: '80vh' });
	const [inputText, setInputText] = useState<string>('');
	const [inputError, setInputError] = useState<string>('');
	const [viewMode, setViewMode] = useState<'form' | 'json'>('form');
	const { data: environments, isLoading: environmentsLoading, error: environmentsError } = useApiEnvironments();
	const client = useApiClient(FlowClient);
	const toastService = useToastService();
	const { ensureSubscribed } = useNotifications();

	const generateSampleInput = React.useCallback(() => {
		if (inputSchemaValue?.Value) {
			const sample = generateSampleJsonFromSchema(inputSchemaValue.Value);
			return JSON.stringify(sample, null, 2);
		}
		return '';
	}, [inputSchemaValue]);

	const formValue = useMemo((): Record<string, unknown> => {
		if (!inputText.trim()) return {};
		try {
			const parsed = JSON.parse(inputText);
			return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) ? parsed : {};
		} catch {
			return {};
		}
	}, [inputText]);

	const handleFormChange = useCallback((value: Record<string, unknown>) => {
		setInputText(JSON.stringify(value, null, 2));
	}, []);

	const isInputValid = useMemo(() => {
		if (!inputSchemaValue?.Value) return true;
		return validateRequiredFields(inputSchemaValue.Value, formValue);
	}, [inputSchemaValue, formValue]);

	// Reset state when modal opens
	useEffect(() => {
		if (open) {
			setSelectedEnvironmentId(initialEnvironmentId || '');
			setIsRunning(false);
			setInputText(generateSampleInput());
			setInputError('');
			setViewMode(inputSchemaValue?.Value ? 'form' : 'json');
		}
	}, [open, initialEnvironmentId, generateSampleInput, inputSchemaValue]);

	useEffect(() => {
		if (open) {
			setRunSynchronous(mode === 'resume');
		}
	}, [open, mode]);

	// Auto-select first environment if available
	useEffect(() => {
		if (environments && environments.length > 0 && !selectedEnvironmentId) {
			setSelectedEnvironmentId(environments[0].id);
		}
	}, [environments, selectedEnvironmentId]);

	// Validate JSON input whenever it changes
	useEffect(() => {
		if (inputText.trim() === '') {
			setInputError('');
			return;
		}

		try {
			JSON.parse(inputText);
			setInputError('');
		} catch (error) {
			setInputError('Invalid JSON format');
		}
	}, [inputText]);

	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setInputText(event.target.value);
	};

	const handleFormatJson = () => {
		if (inputText.trim() === '') return;

		try {
			const parsed = JSON.parse(inputText);
			const formatted = JSON.stringify(parsed, null, 2);
			setInputText(formatted);
			setInputError('');
		} catch (error) {
			// Keep the error state, don't modify the text
		}
	};

	const handleRun = async () => {
		if (!selectedEnvironmentId) return;

		setIsRunning(true);
		try {
			const notificationDefinitionId = mode === 'run' ? flowId : definitionId;
			if (!notificationDefinitionId) {
				throw new Error('The flow definition is not available yet.');
			}
			await ensureSubscribed('flowDefinition', notificationDefinitionId);
			let response;

			if (mode === 'resume') {
				response = await client.resumeInstance(flowId, {
					runSynchronous: runSynchronous,
					storeTransitions: storeTransitions,
					storeVariables: storeVariables,
				});
			} else {
				// Parse input if provided
				let parsedInput = null;
				if (inputText.trim() !== '') {
					try {
						parsedInput = JSON.parse(inputText);
					} catch (error) {
						// Should not happen due to validation, but just in case
						toastService.error('Invalid JSON input');
						return;
					}
				}

				response = await client.createInstance({
					environmentId: selectedEnvironmentId,
					definitionId: flowId,
					input: parsedInput,
					runSynchronous: runSynchronous,
					storeTransitions: storeTransitions,
					storeVariables: storeVariables,
				});
			}

			console.log('Flow instance response', response);
			toastService.success('Flow status: ' + response.status);
			onSuccess?.(response.instanceId); // Pass the instance ID to the callback
			onClose(); // Close modal on success
		} catch (error) {
			toastService.error(error instanceof Error ? error.message : mode === 'resume' ? 'Failed to resume flow' : 'Failed to start flow');
			console.error('Flow execution error:', error);
			onClose(); // Close modal on failure
		} finally {
			setIsRunning(false);
		}
	};

	const handleCancel = () => {
		if (!isRunning) {
			onClose();
		}
	};

	return (
		<Dialog
			open={open}
			onClose={handleCancel}
			fullScreen={isMaximized}
			fullWidth
			maxWidth={false}
			sx={{ zIndex: 1400 }}
			PaperProps={{
				ref: paperRef,
				sx: isMaximized
					? { width: '100vw', height: '100vh', maxWidth: '100vw', maxHeight: '100vh', resize: 'none', overflow: 'hidden' }
					: { width: storedSize.width, height: storedSize.height, maxWidth: '95vw', maxHeight: '90vh', resize: 'both', overflow: 'hidden' },
			}}
		>
			<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
				<Typography component="span" variant="h6" sx={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
					{mode === 'resume' ? 'Resume Flow' : 'Run Flow'}: {flowName}
				</Typography>
				<DialogHeaderActions
					isMaximized={isMaximized}
					onToggleMaximized={() => setIsMaximized((value) => !value)}
					onClose={handleCancel}
					closeDisabled={isRunning}
					iconButtonSx={dialogHeaderIconButtonSx}
				/>
			</DialogTitle>
			<DialogContent dividers sx={{ flex: 1, overflowY: 'auto' }}>
				<Stack spacing={3} sx={{ pt: 1 }}>
					{mode === 'run' && (
						<>
							<Typography variant="body2" color="text.secondary">
								Select an environment to run the flow in:
							</Typography>
							{environmentsError ? (
								<Alert severity="error">Failed to load environments. Please try again.</Alert>
							) : (
								<FormControl fullWidth>
									<InputLabel id="environment-select-label">Environment</InputLabel>
									<Select
										labelId="environment-select-label"
										value={selectedEnvironmentId}
										onChange={(event) => setSelectedEnvironmentId(event.target.value as string)}
										label="Environment"
										disabled={isRunning || environmentsLoading}
										MenuProps={selectMenuProps}
									>
										{environmentsLoading && (
											<MenuItem key="loading" value="" disabled>
												Loading...
											</MenuItem>
										)}
										{environments && environments.length === 0 && (
											<MenuItem key="no-environment" value="" disabled>
												No environments available
											</MenuItem>
										)}
										{environments &&
											environments.map((environment: EnvironmentModel) => (
												<MenuItem key={environment.id} value={environment.id}>
													{environment.name}
												</MenuItem>
											))}
									</Select>
								</FormControl>
							)}
						</>
					)}
					<FormControlLabel
						control={<Checkbox checked={runSynchronous} onChange={(e) => setRunSynchronous(e.target.checked)} disabled={isRunning} />}
						label="Run Synchronously"
					/>
					<FormControlLabel
						control={<Checkbox checked={storeTransitions} onChange={(e) => setStoreTransitions(e.target.checked)} disabled={isRunning} />}
						label="Store Transitions"
					/>
					<FormControlLabel
						control={<Checkbox checked={storeVariables} onChange={(e) => setStoreVariables(e.target.checked)} disabled={isRunning} />}
						label="Store Variables"
					/>
					{mode === 'resume' && (
						<Box sx={{ position: 'relative' }}>
							<TextField
								label="Parent Variable Scope (JSON)"
								placeholder='{"variableName": "value"}'
								multiline
								rows={10}
								fullWidth
								value={inputText}
								onChange={handleInputChange}
								disabled={isRunning}
								error={!!inputError}
								helperText={inputError || 'Optional: Enter valid JSON for parent variable scope'}
							/>
							<Tooltip title="Format JSON">
								<span
									style={{
										position: 'absolute',
										top: 8,
										right: 8,
									}}
								>
									<IconButton onClick={handleFormatJson} disabled={isRunning || inputText.trim() === '' || !!inputError} size="small">
										<FormatAlignJustifyIcon fontSize="small" />
									</IconButton>
								</span>
							</Tooltip>
						</Box>
					)}
					{mode === 'run' && (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
							{/* View mode toggle — only shown when a schema is available */}
							{inputSchemaValue?.Value && (
								<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
									<Typography variant="caption" color="text.secondary">
										Input ({inputSchemaValue.Value.title ?? inputSchemaValue.Name ?? 'schema'})
									</Typography>
									<ToggleButtonGroup value={viewMode} exclusive onChange={(_, val) => val && setViewMode(val)} size="small" disabled={isRunning}>
										<ToggleButton value="form" sx={{ px: 2 }}>
											Form
										</ToggleButton>
										<ToggleButton value="json" sx={{ px: 2 }}>
											JSON
										</ToggleButton>
									</ToggleButtonGroup>
								</Box>
							)}

							{/* Structured form editor */}
							{viewMode === 'form' && inputSchemaValue?.Value && (
								<JsonObjectInput schema={inputSchemaValue.Value} value={formValue} onChange={handleFormChange} />
							)}

							{/* Raw JSON editor */}
							{viewMode === 'json' && (
								<Box sx={{ position: 'relative' }}>
									<TextField
										label={inputSchemaValue?.Value ? undefined : 'Input (JSON)'}
										placeholder='{"key": "value"}'
										multiline
										rows={10}
										fullWidth
										value={inputText}
										onChange={handleInputChange}
										disabled={isRunning}
										error={!!inputError}
										helperText={
											inputError || (inputSchemaValue?.Value ? 'Enter valid JSON input for the flow' : 'Optional: Enter valid JSON input for the flow')
										}
									/>
									<Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
										{inputSchemaValue?.Value && (
											<Tooltip title="Reset to sample JSON">
												<span>
													<IconButton onClick={() => setInputText(generateSampleInput())} disabled={isRunning} size="small">
														<RestoreIcon fontSize="small" />
													</IconButton>
												</span>
											</Tooltip>
										)}
										<Tooltip title="Format JSON">
											<span>
												<IconButton onClick={handleFormatJson} disabled={isRunning || inputText.trim() === '' || !!inputError} size="small">
													<FormatAlignJustifyIcon fontSize="small" />
												</IconButton>
											</span>
										</Tooltip>
									</Box>
								</Box>
							)}
						</Box>
					)}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleCancel} disabled={isRunning}>
					Cancel
				</Button>
				<Button
					onClick={handleRun}
					variant="contained"
					disabled={!selectedEnvironmentId || isRunning || environmentsLoading || !!inputError || !isInputValid}
					startIcon={isRunning ? <CircularProgress size={16} /> : undefined}
				>
					{isRunning ? (mode === 'resume' ? 'Resuming...' : 'Running...') : mode === 'resume' ? 'Resume Flow' : 'Run Flow'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
